import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; // Assuming you're using React Router

export default function MembershipUserPage() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [enrollmentData, setEnrollmentData] = useState({
    name: "",
    email: "",
    phone: "",
    background: "",
    goals: ""
  });
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate(); // For navigation

  const memberships = [
    {
      id: 1,
      name: "Basic Membership",
      type: "Basic",
      price: "Free",
      instructor: "Dr. Rajesh Sharma",
      instructorBio: "15+ years in veterinary education, specialized in clinical training",
      description: "Perfect for beginners to start learning. Includes limited course access and community support.",
      shortDescription: "Essential veterinary knowledge for beginners and students",
      coverImage: "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=600&q=60",
      promoVideo: "https://www.youtube.com/embed/J4kzKpMP6o8",
      courses: [
        {
          title: "Animal Nutrition 101",
          duration: "4 weeks",
          level: "Beginner",
          lessons: 12,
          objectives: [
            "Learn the fundamentals of nutrition",
            "Understand diet formulation",
            "Identify nutritional requirements",
            "Plan balanced diets for pets"
          ],
          resources: ["Nutrition Charts", "Diet Plans", "Quizzes", "Case Studies"]
        },
        {
          title: "Basic Veterinary Practices",
          duration: "3 weeks",
          level: "Beginner",
          lessons: 8,
          objectives: [
            "Handling animals safely",
            "Common treatment methods",
            "Basic examination techniques",
            "Medication administration"
          ],
          resources: ["Video Demonstrations", "Checklists", "Practical Exercises"]
        }
      ],
      curriculum: [
        { name: "Animal Nutrition 101", duration: "4 weeks", type: "core" },
        { name: "Basic Veterinary Practices", duration: "3 weeks", type: "core" },
        { name: "Animal Behavior Basics", duration: "2 weeks", type: "elective" }
      ],
      features: [
        { name: "Course Certificates", icon: "🎓", included: true },
        { name: "Downloadable Resources", icon: "📥", included: true },
        { name: "Community Access", icon: "👥", included: true },
        { name: "Email Support", icon: "📧", included: true },
        { name: "Live Q&A Sessions", icon: "🎥", included: false },
        { name: "1-on-1 Mentoring", icon: "🎯", included: false }
      ],
      rating: 4.5,
      reviews: 234,
      students: 1250,
      accessExpiration: "Lifetime",
      category: "Clinical Foundations",
      tags: ["beginner", "nutrition", "fundamentals"],
      whatsIncluded: [
        "Lifetime access to basic courses",
        "8+ hours of video content",
        "12 downloadable resources",
        "Mobile app access",
        "Certificate of completion"
      ],
      intendedLearners: ["Veterinary Students", "Animal Care Assistants", "Pet Owners", "Shelter Staff"]
    },
    {
      id: 2,
      name: "Pro Membership",
      type: "Pro",
      price: "₹999 / month",
      instructor: "Dr. Sneha Patel",
      instructorBio: "Board-certified surgeon with expertise in advanced veterinary procedures",
      description: "For advanced learners looking for deeper knowledge, mentorship sessions, and priority support.",
      shortDescription: "Advanced clinical training for practicing veterinarians",
      coverImage: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=600&q=60",
      promoVideo: "https://www.youtube.com/embed/0e3GPea1Tyg",
      courses: [
        {
          title: "Advanced Clinical Diagnosis",
          duration: "6 weeks",
          level: "Advanced",
          lessons: 18,
          objectives: [
            "Diagnostic imaging overview",
            "Interpretation of test results",
            "Differential diagnosis",
            "Case management strategies"
          ],
          resources: ["Image Library", "Case Studies", "Diagnostic Guides", "Expert Reviews"]
        },
        {
          title: "Surgical Techniques Mastery",
          duration: "5 weeks",
          level: "Advanced",
          lessons: 15,
          objectives: [
            "Advanced surgical procedures",
            "Sterile technique mastery",
            "Post-operative care",
            "Complication management"
          ],
          resources: ["Surgical Videos", "Procedure Manuals", "Simulation Tools", "Expert Feedback"]
        }
      ],
      curriculum: [
        { name: "Advanced Clinical Diagnosis", duration: "6 weeks", type: "core" },
        { name: "Surgical Techniques Mastery", duration: "5 weeks", type: "core" },
        { name: "Emergency Medicine", duration: "4 weeks", type: "core" },
        { name: "Pharmacology Updates", duration: "3 weeks", type: "elective" }
      ],
      features: [
        { name: "Course Certificates", icon: "🎓", included: true },
        { name: "Downloadable Resources", icon: "📥", included: true },
        { name: "Community Access", icon: "👥", included: true },
        { name: "Email Support", icon: "📧", included: true },
        { name: "Live Q&A Sessions", icon: "🎥", included: true },
        { name: "1-on-1 Mentoring", icon: "🎯", included: true }
      ],
      rating: 4.8,
      reviews: 189,
      students: 842,
      accessExpiration: "Monthly",
      category: "Advanced Clinical",
      tags: ["surgery", "diagnostics", "advanced", "clinical"],
      whatsIncluded: [
        "Monthly access to all courses",
        "25+ hours of video content",
        "30+ downloadable resources",
        "Weekly live sessions",
        "Personal mentoring",
        "Advanced certificates"
      ],
      intendedLearners: ["Practicing Veterinarians", "Clinical Staff", "Surgical Assistants", "Veterinary Technicians"]
    },
    {
      id: 3,
      name: "Enterprise Membership",
      type: "Enterprise",
      price: "₹4999 / year",
      instructor: "Dr. Aman Verma",
      instructorBio: "Veterinary practice management expert with 20+ years consulting experience",
      description: "Designed for professionals and institutions. Get full access to all resources, mentorships, and lifetime certificates.",
      shortDescription: "Complete practice management for veterinary hospitals",
      coverImage: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=600&q=60",
      promoVideo: "https://www.youtube.com/embed/ScMzIvxBSi4",
      courses: [
        {
          title: "Practice Management Excellence",
          duration: "8 weeks",
          level: "Advanced",
          lessons: 24,
          objectives: [
            "Staff training and development",
            "Client relationship management",
            "Financial planning and analysis",
            "Operational efficiency optimization"
          ],
          resources: ["Management Templates", "Financial Models", "Staff Training Kits", "Consultation Sessions"]
        },
        {
          title: "Veterinary Leadership Program",
          duration: "6 weeks",
          level: "Advanced",
          lessons: 18,
          objectives: [
            "Team management strategies",
            "Ethics in veterinary practice",
            "Business development",
            "Quality control systems"
          ],
          resources: ["Leadership Guides", "Ethics Case Studies", "Business Plans", "Team Analytics"]
        }
      ],
      curriculum: [
        { name: "Practice Management Excellence", duration: "8 weeks", type: "core" },
        { name: "Veterinary Leadership Program", duration: "6 weeks", type: "core" },
        { name: "Advanced Business Development", duration: "4 weeks", type: "core" },
        { name: "Team Building Strategies", duration: "3 weeks", type: "elective" }
      ],
      features: [
        { name: "Course Certificates", icon: "🎓", included: true },
        { name: "Downloadable Resources", icon: "📥", included: true },
        { name: "Community Access", icon: "👥", included: true },
        { name: "Email Support", icon: "📧", included: true },
        { name: "Live Q&A Sessions", icon: "🎥", included: true },
        { name: "1-on-1 Mentoring", icon: "🎯", included: true },
        { name: "Dedicated Account Manager", icon: "🤝", included: true },
        { name: "Team Analytics Dashboard", icon: "📊", included: true }
      ],
      rating: 4.9,
      reviews: 67,
      students: 156,
      accessExpiration: "Yearly",
      category: "Practice Management",
      tags: ["management", "business", "enterprise", "leadership"],
      whatsIncluded: [
        "Yearly access to all courses",
        "40+ hours of video content",
        "50+ downloadable resources",
        "Dedicated account manager",
        "Team analytics dashboard",
        "Custom certificates",
        "Priority 24/7 support"
      ],
      intendedLearners: ["Veterinary Hospitals", "Practice Managers", "Training Coordinators", "Corporate Chains"]
    }
  ];

  // Consolidated Styling
  const styles = {
    // Page Styles
    page: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)",
      padding: "40px 20px 40px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      position: "relative",
    },

    // Page Header with Cross Button
    pageHeader: {
      position: "absolute",
      top: "20px",
      right: "20px",
      zIndex: 100,
    },

    // Cross Button for Main Page
    pageCrossButton: {
      width: "50px",
      height: "50px",
      borderRadius: "50%",
      background: "rgba(255, 255, 255, 0.9)",
      border: "0px solid #e2e8f0",
      color: "#64748b",
      fontSize: "1.5rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      // boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
    },
    pageCrossButtonHover: {
      background: "#ef4444",
      color: "#fff",
      borderColor: "#ef4444",
      transform: "rotate(90deg) scale(1.1)",
      boxShadow: "0 6px 20px rgba(239, 68, 68, 0.3)",
    },

    title: {
      textAlign: "center",
      fontSize: "3rem",
      fontWeight: "800",
      background: "linear-gradient(90deg, #ff7e5f, #8e44ad)",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
      // marginBottom: "10px",
      // paddingTop: "10px",
    },
    subtitle: {
      textAlign: "center",
      color: "#64748b",
       marginBottom: "10px",
      fontSize: "1.1rem",
      maxWidth: "600px",
      margin: "0 auto",
      lineHeight: "1.6",
    },

    // Card Grid
    cardGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: "30px",
      maxWidth: "1200px",
      margin: "0 auto 60px",
    },

    // Card Styles
    card: {
      background: "rgba(255, 255, 255, 0.95)",
      borderRadius: "20px",
      padding: "25px",
      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      border: "0px solid transparent",   // No border initially
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },

    cardHover: {
      transform: "translateY(-10px)",
      boxShadow: "0 20px 40px rgba(37, 99, 235, 0.15)",
      border: "2px solid #8e44ad",        // Purple border on hover
    },


    // Card Elements
    image: {
      width: "100%",
      height: "180px",
      objectFit: "cover",
      borderRadius: "15px",
      marginBottom: "20px",
    },
    planName: {
      fontSize: "1.5rem",
      fontWeight: "700",
      background: "linear-gradient(90deg, #ff7e5f, #8e44ad)",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
      marginBottom: "8px",
    },
    price: {
      fontWeight: "700",
      fontSize: "1.4rem",
      color: "#ff7e5f",
      marginBottom: "5px",
    },
    instructor: {
      color: "#64748b",
      fontSize: "0.9rem",
      marginBottom: "15px",
      display: "flex",
      alignItems: "center",
      gap: "5px",
    },
    desc: {
      color: "#475569",
      margin: "15px 0 20px",
      lineHeight: "1.6",
      flex: "1",
      fontSize: "0.95rem",
    },

    // Badge Styles
    badge: {
      display: "inline-block",
      padding: "6px 12px",
      borderRadius: "20px",
      fontSize: "0.75rem",
      fontWeight: "600",
      marginRight: "8px",
      marginBottom: "10px",
    },

    // Button Styles
    btn: {
      padding: "14px 20px",
      background: "linear-gradient(90deg, #ff7e5f, #8e44ad)",
      color: "#fff",
      fontWeight: "600",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "1rem",
      transition: "all 0.3s ease",
      marginTop: "15px",
    },
    backBtn: {
      padding: "10px 20px",
      background: "transparent",
      color: "#64748b",
      fontWeight: "600",
      border: "2px solid #e2e8f0",
      borderRadius: "10px",
      cursor: "pointer",
      fontSize: "0.95rem",
      transition: "all 0.2s ease",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnHover: {
      background: "linear-gradient(90deg, #ff7e5f, #8e44ad)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 20px rgba(136, 4, 212, 0.3)",
    },
    backBtnHover: {
      background: "#f1f5f9",
      borderColor: "#cbd5e1",
      transform: "translateX(-3px)",
    },

    // Modal Backdrop
    modalBackdrop: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000,
      padding: "20px",
      backdropFilter: "blur(4px)",
    },

    // Modal Container
    modal: {
      background: "#fff",
      borderRadius: "24px",
      width: "100%",
      maxWidth: "900px",
      maxHeight: "90vh",
      overflow: "hidden",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      display: "flex",
      flexDirection: "column",
    },

    // Modal Header
    modalHeader: {
      padding: "25px 30px 20px",
      borderBottom: "1px solid #e2e8f0",
      position: "relative",
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    },

    // Modal Close Button
    modalClose: {
      position: "absolute",
      right: "2px",
      top: "2px",
      width: "30px",
      height: "30px",
      borderRadius: "50%",
      background: "rgba(255, 255, 255, 0.9)",
      border: "0px solid #e2e8f0",
      color: "#64748b",
      fontSize: "1.2rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      zIndex: 10,
    },
    modalCloseHover: {
      background: "#ef4444",
      color: "#fff",
      borderColor: "#ef4444",
      transform: "rotate(90deg)",
    },

    // Modal Body
    modalBody: {
      padding: "30px",
      overflowY: "auto",
      flex: "1",
    },

    // Tab Styles
    tabsContainer: {
      display: "flex",
      borderBottom: "2px solid #e2e8f0",
      marginBottom: "30px",
      gap: "10px",
    },
    tab: {
      padding: "12px 24px",
      background: "none",
      border: "none",
      fontSize: "0.95rem",
      fontWeight: "600",
      color: "#64748b",
      cursor: "pointer",
      position: "relative",
      transition: "all 0.2s ease",
    },
    tabActive: {
      color: "#8e44ad",
    },
    tabIndicator: {
      position: "absolute",
      bottom: "-2px",
      left: 0,
      right: 0,
      height: "2px",
      background: "#8e44ad",
    },

    // Section Titles
    sectionTitle: {
      fontSize: "1.3rem",
      fontWeight: "700",
      color: "#1e293b",
      margin: "25px 0 15px",
      paddingBottom: "10px",
      borderBottom: "2px solid #f1f5f9",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },

    // Feature Grid
    featureGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "15px",
      margin: "20px 0",
    },
    featureItem: {
      padding: "15px",
      background: "#f8fafc",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },

    // Course Card
    courseCard: {
      background: "#f8fafc",
      borderRadius: "12px",
      padding: "20px",
      marginBottom: "20px",
      borderLeft: "4px solid #ff7e5f",
    },

    // Form Styles
    formGroup: {
      marginBottom: "20px",
    },
    formLabel: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "600",
      color: "#1e293b",
    },
    formInput: {
      width: "100%",
      display: "block",
      padding: "12px 16px",
      border: "1.5px solid #e2e8f0",
      borderRadius: "10px",
      fontSize: "1rem",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
      outline: "none",
      background: "#fff"
    },
    formInputFocus: {
      borderColor: "#8e44ad",
      boxShadow: "0 0 0 3px rgba(142, 68, 173, 0.2)",
      background: "#fff",
    },


    // Stats Container
    statsContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "20px",
      margin: "25px 0",
      padding: "20px",
      background: "#f8fafc",
      borderRadius: "15px",
    },
    statItem: {
      textAlign: "center",
    },
    statValue: {
      fontSize: "1.8rem",
      fontWeight: "700",
      color: "#ff7e5f",
      marginBottom: "5px",
    },
    statLabel: {
      fontSize: "0.9rem",
      color: "#64748b",
    },

    // Payment Summary
    paymentSummary: {
      background: "#f8fafc",
      borderRadius: "15px",
      padding: "25px",
      margin: "25px 0",
      border: "1px solid #e2e8f0",
    },

    // Navigation Back Button in Modal
    modalBackButton: {
      position: "absolute",
      left: "25px",
      top: "25px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "rgba(255, 255, 255, 0.9)",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      padding: "8px 16px",
      color: "#64748b",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      zIndex: 10,
    },
    modalBackButtonHover: {
      background: "#f1f5f9",
      borderColor: "#cbd5e1",
      transform: "translateX(-3px)",
    },
  };

  const [hoveredCard, setHoveredCard] = useState(null);
  const [closeHover, setCloseHover] = useState(false);
  const [backHover, setBackHover] = useState(false);
  const [pageCrossHover, setPageCrossHover] = useState(false);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<span key={i} style={{ color: "#fbbf24" }}>★</span>);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<span key={i} style={{ color: "#fbbf24" }}>☆</span>);
      } else {
        stars.push(<span key={i} style={{ color: "#e2e8f0" }}>★</span>);
      }
    }
    return stars;
  };

  const handleEnrollmentSubmit = (e) => {
    e.preventDefault();
    if (selectedPlan.price === "Free") {
      alert("🎉 Your enrollment request has been submitted for admin approval!");
      setSelectedPlan(null);
    } else {
      setPaymentModal(selectedPlan);
    }
  };

  const handlePaymentSubmit = () => {
    alert(`✅ Payment successful! You are now enrolled in ${paymentModal.name}.`);
    setPaymentModal(null);
    setSelectedPlan(null);
  };

  // Function to handle going back to previous page
  const handleGoBack = () => {
    // If you're using React Router
    navigate(-1); // Goes back one step in history

    // If you're not using React Router, you can use:
    // window.history.back();
  };

  return (
    <div style={styles.page}>
      {/* Cross Button on Main Page */}
      <div style={styles.pageHeader}>
        <button
          style={{
            ...styles.pageCrossButton,
            ...(pageCrossHover ? styles.pageCrossButtonHover : {}),
          }}
          onClick={handleGoBack}
          onMouseEnter={() => setPageCrossHover(true)}
          onMouseLeave={() => setPageCrossHover(false)}
          title="Close Membership Page"
        >
          ✕
        </button>
      </div>

      <h1 style={styles.title}>VetLearn Membership Plans</h1>
      <p style={styles.subtitle}>
        Choose from our expertly crafted membership tiers designed to match your veterinary career goals.
        Transform your skills with structured learning paths and expert mentorship.
      </p>

      {/* Membership Cards Grid */}
      <div style={styles.cardGrid}>
        {memberships.map((plan) => (
          <div
            key={plan.id}
            style={{
              ...styles.card,
              ...(hoveredCard === plan.id ? styles.cardHover : {}),
            }}
            onMouseEnter={() => setHoveredCard(plan.id)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <img src={plan.coverImage} alt={plan.name} style={styles.image} />
            <div style={styles.planName}>{plan.name}</div>
            <div style={styles.price}>{plan.price}</div>
            <div style={styles.instructor}>
              <span style={{ color: "#ff7e5f" }}>👨‍🏫</span>
              Instructor: <strong>{plan.instructor}</strong>
            </div>
            <p style={styles.desc}>{plan.description}</p>

            <div style={{ marginTop: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                {renderStars(plan.rating)}
                <span style={{ marginLeft: "8px", color: "#64748b", fontSize: "0.9rem" }}>
                  ({plan.rating}) • {plan.students} students
                </span>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "15px" }}>
                {plan.tags.slice(0, 3).map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      ...styles.badge,
                      background: idx === 0 ? "#dbeafe" : "#f1f5f9",
                      color: idx === 0 ? "#ff7e5f" : "#695b47ff",
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <button
              style={{ ...styles.btn, width: "100%" }}
              onMouseEnter={(e) => {
                e.target.style.background = "linear-gradient(90deg, #f74d23ff, #633079ff)";
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 8px 20px rgba(83, 6, 146, 0.22)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "linear-gradient(90deg, #ff7e5f, #8e44ad)";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
              onClick={() => {
                setSelectedPlan(plan);
                setActiveTab("overview");
              }}
            >
              View Details & Enroll
            </button>
          </div>
        ))}
      </div>

      {/* Plan Details Modal */}
      {selectedPlan && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              {/* Back to Main Page Button */}


              {/* Close Modal Button */}
              <button
                style={{
                  ...styles.modalClose,
                  ...(closeHover ? styles.modalCloseHover : {}),
                }}
                onClick={() => setSelectedPlan(null)}
                onMouseEnter={() => setCloseHover(true)}
                onMouseLeave={() => setCloseHover(false)}
              >
                ✕
              </button>

              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                  <h2 style={styles.planName}>{selectedPlan.name}</h2>
                  <div style={{ display: "flex", alignItems: "center", gap: "15px", marginTop: "10px" }}>
                    <span style={styles.price}>{selectedPlan.price}</span>
                    <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                      <strong>Access:</strong> {selectedPlan.accessExpiration}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                      <strong>Category:</strong> {selectedPlan.category}
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {renderStars(selectedPlan.rating)}
                  <span style={{ marginLeft: "8px", color: "#64748b" }}>
                    {selectedPlan.rating} ({selectedPlan.reviews} reviews)
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {/* Tabs Navigation */}
              <div style={styles.tabsContainer}>
                {["overview", "curriculum", "instructor", "enroll"].map((tab) => (
                  <button
                    key={tab}
                    style={{
                      ...styles.tab,
                      ...(activeTab === tab ? styles.tabActive : {}),
                    }}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {activeTab === tab && <span style={styles.tabIndicator} />}
                  </button>
                ))}
              </div>

              {/* Overview Tab */}
              {activeTab === "overview" && (
                <div>
                  <p style={{ color: "#475569", lineHeight: "1.8", marginBottom: "25px" }}>
                    {selectedPlan.description}
                  </p>

                  {/* Stats */}
                  <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                      <div style={styles.statValue}>{selectedPlan.students}+</div>
                      <div style={styles.statLabel}>Students Enrolled</div>
                    </div>
                    <div style={styles.statItem}>
                      <div style={styles.statValue}>{selectedPlan.courses.length}</div>
                      <div style={styles.statLabel}>Courses Included</div>
                    </div>
                    <div style={styles.statItem}>
                      <div style={styles.statValue}>{selectedPlan.accessExpiration}</div>
                      <div style={styles.statLabel}>Access Duration</div>
                    </div>
                    <div style={styles.statItem}>
                      <div style={styles.statValue}>{selectedPlan.rating}/5</div>
                      <div style={styles.statLabel}>Average Rating</div>
                    </div>
                  </div>

                  {/* What's Included */}
                  <div>
                    <h3 style={styles.sectionTitle}>🎁 What's Included</h3>
                    <div style={styles.featureGrid}>
                      {selectedPlan.whatsIncluded.map((item, idx) => (
                        <div key={idx} style={styles.featureItem}>
                          <span style={{ fontSize: "1.2rem" }}>✓</span>
                          <span style={{ color: "#1e293b" }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <h3 style={styles.sectionTitle}>✨ Features & Benefits</h3>
                    <div style={styles.featureGrid}>
                      {selectedPlan.features.map((feature, idx) => (
                        <div key={idx} style={styles.featureItem}>
                          <span style={{ fontSize: "1.2rem" }}>{feature.icon}</span>
                          <div>
                            <div style={{ fontWeight: "600", color: "#1e293b" }}>{feature.name}</div>
                            <div style={{
                              fontSize: "0.85rem",
                              color: feature.included ? "#10b981" : "#94a3b8"
                            }}>
                              {feature.included ? "✓ Included" : "✗ Not included"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Intended Learners */}
                  <div>
                    <h3 style={styles.sectionTitle}>🎯 Intended For</h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {selectedPlan.intendedLearners.map((learner, idx) => (
                        <span
                          key={idx}
                          style={{
                            ...styles.badge,
                            background: "#ebd5b3ff",
                            color: "#ff7e5f",
                            border: "1px solid #bae6fd",
                          }}
                        >
                          {learner}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Curriculum Tab */}
              {activeTab === "curriculum" && (
                <div>
                  <h3 style={styles.sectionTitle}>📚 Course Curriculum</h3>
                  {selectedPlan.curriculum.map((course, idx) => (
                    <div
                      key={idx}
                      style={{
                        ...styles.courseCard,
                        borderLeftColor: course.type === "core" ? "#ff7e5f" : "#8e44ad",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <h4 style={{ fontWeight: "600", color: "#1e293b", marginBottom: "5px" }}>
                            {course.name}
                          </h4>
                          <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "10px" }}>
                            <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                              ⏱️ {course.duration}
                            </span>
                            <span style={{
                              ...styles.badge,
                              background: course.type === "core" ? "#dbeafe" : "#f1f5f9",
                              color: course.type === "core" ? "#ff7e5f" : "#475569",
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                            }}>
                              {course.type === "core" ? "Core Course" : "Elective"}
                            </span>
                          </div>
                        </div>
                        <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                          Module {idx + 1}
                        </span>
                      </div>
                    </div>
                  ))}

                  <h3 style={styles.sectionTitle}>🎯 Featured Courses</h3>
                  {selectedPlan.courses.map((course, idx) => (
                    <div key={idx} style={styles.courseCard}>
                      <h4 style={{ fontWeight: "600", color: "#1e293b", marginBottom: "10px" }}>
                        {course.title}
                      </h4>
                      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "15px" }}>
                        <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                          ⏱️ {course.duration} • 📚 {course.lessons} lessons
                        </span>
                        <span style={{
                          ...styles.badge,
                          background: course.level === "Beginner" ? "#dcfce7" :
                            course.level === "Intermediate" ? "#fef3c7" : "#fee2e2",
                          color: course.level === "Beginner" ? "#166534" :
                            course.level === "Intermediate" ? "#92400e" : "#991b1b",
                        }}>
                          {course.level}
                        </span>
                      </div>

                      <div style={{ marginBottom: "15px" }}>
                        <strong style={{ color: "#475569", display: "block", marginBottom: "8px" }}>
                          Learning Objectives:
                        </strong>
                        <ul style={{ margin: 0, paddingLeft: "20px", color: "#64748b" }}>
                          {course.objectives.map((obj, objIdx) => (
                            <li key={objIdx} style={{ marginBottom: "4px" }}>{obj}</li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <strong style={{ color: "#475569", display: "block", marginBottom: "8px" }}>
                          Resources Included:
                        </strong>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {course.resources.map((resource, resIdx) => (
                            <span
                              key={resIdx}
                              style={{
                                ...styles.badge,
                                background: "#f1f5f9",
                                color: "#475569",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              {resource}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Instructor Tab */}
              {activeTab === "instructor" && (
                <div>
                  <div style={{ display: "flex", gap: "25px", marginBottom: "30px" }}>
                    <div style={{
                      width: "120px",
                      height: "120px",
                      borderRadius: "50%",
                      background: "linear-gradient(90deg, #ff7e5f, #8e44ad)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "2.5rem",
                      fontWeight: "700",
                      flexShrink: 0,
                    }}>
                      {selectedPlan.instructor.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1e293b", marginBottom: "10px" }}>
                        {selectedPlan.instructor}
                      </h3>
                      <p style={{ color: "#475569", lineHeight: "1.6", marginBottom: "20px" }}>
                        {selectedPlan.instructorBio}
                      </p>
                      <div style={{ display: "flex", gap: "25px" }}>
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: "700", color: "#ff7e5f" }}>
                            {selectedPlan.rating}/5
                          </div>
                          <div style={{ fontSize: "0.9rem", color: "#64748b" }}>Instructor Rating</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: "700", color: "#8e44ad" }}>
                            {selectedPlan.students}+
                          </div>
                          <div style={{ fontSize: "0.9rem", color: "#64748b" }}>Students Taught</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: "700", color: "#ff7e5f" }}>
                            {selectedPlan.courses.length}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: "#64748b" }}>Courses Created</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 style={styles.sectionTitle}>📝 Teaching Philosophy</h3>
                    <p style={{ color: "#475569", lineHeight: "1.8" }}>
                      With extensive experience in veterinary education and clinical practice,
                      {selectedPlan.instructor.split(' ')[0]} brings real-world expertise and practical
                      knowledge to every course. Committed to advancing veterinary education through
                      innovative teaching methods and hands-on learning experiences.
                    </p>
                  </div>
                </div>
              )}

              {/* Enroll Tab */}
              {activeTab === "enroll" && (
                <div>
                  <h3 style={styles.sectionTitle}>📝 Enrollment Application</h3>
                  <p style={{ color: "#64748b", marginBottom: "25px" }}>
                    Complete the form below to apply for enrollment. {selectedPlan.price === "Free"
                      ? "Free memberships require admin approval."
                      : "Payment will be required after admin approval."}
                  </p>

                  <form onSubmit={handleEnrollmentSubmit}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Full Name *</label>
                        <input
                          type="text"
                          required
                          style={
                            focusedField === "name"
                              ? { ...styles.formInput, ...styles.formInputFocus }
                              : styles.formInput
                          }
                          onFocus={() => setFocusedField("name")}
                          onBlur={() => setFocusedField(null)}

                          value={enrollmentData.name}
                          onChange={(e) => setEnrollmentData({ ...enrollmentData, name: e.target.value })}
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Email Address *</label>
                        <input
                          type="email"
                          required
                          style={
                            focusedField === "email"
                              ? { ...styles.formInput, ...styles.formInputFocus }
                              : styles.formInput
                          }
                          onFocus={() => setFocusedField("email")}
                          onBlur={() => setFocusedField(null)}
                          value={enrollmentData.email}
                          onChange={(e) => setEnrollmentData({ ...enrollmentData, email: e.target.value })}
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Phone Number *</label>
                        <input
                          type="tel"
                          required
                          style={
                            focusedField === "tel"
                              ? { ...styles.formInput, ...styles.formInputFocus }
                              : styles.formInput
                          }
                          onFocus={() => setFocusedField("tel")}
                          onBlur={() => setFocusedField(null)}
                          value={enrollmentData.phone}
                          onChange={(e) => setEnrollmentData({ ...enrollmentData, phone: e.target.value })}
                          placeholder="Enter your phone number"
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Current Position</label>
                        <input
                          type="text"
                          style={
                            focusedField === "position"
                              ? { ...styles.formInput, ...styles.formInputFocus }
                              : styles.formInput
                          }
                          onFocus={() => setFocusedField("position")}
                          onBlur={() => setFocusedField(null)}
                          placeholder="e.g., Veterinary Assistant, Student, etc."
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Professional Background *</label>
                      <textarea
                        required
                        rows="3"
                        style={
                          focusedField === "professionalBackground"
                            ? { ...styles.formInput, ...styles.formInputFocus }
                            : styles.formInput
                        }
                        onFocus={() => setFocusedField("professionalBackground")}
                        onBlur={() => setFocusedField(null)}
                        value={enrollmentData.background}
                        onChange={(e) => setEnrollmentData({ ...enrollmentData, background: e.target.value })}
                        placeholder="Tell us about your veterinary background, education, and experience..."
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Learning Objectives & Career Goals *</label>
                      <textarea
                        required
                        rows="3"
                        style={
                          focusedField === "goals"
                            ? { ...styles.formInput, ...styles.formInputFocus }
                            : styles.formInput
                        }
                        onFocus={() => setFocusedField("goals")}
                        onBlur={() => setFocusedField(null)}
                        value={enrollmentData.goals}
                        onChange={(e) => setEnrollmentData({ ...enrollmentData, goals: e.target.value })}
                        placeholder="What specific skills do you want to develop? How will this membership help your career?"
                      />
                    </div>

                    {/* Membership Summary */}
                    <div style={styles.paymentSummary}>
                      <h4 style={{ marginBottom: "15px", color: "#1e293b" }}>📋 Membership Summary</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                        <div>
                          <div style={{ color: "#64748b", fontSize: "0.9rem" }}>Plan Type</div>
                          <div style={{ fontWeight: "600", color: "#1e293b" }}>{selectedPlan.name}</div>
                        </div>
                        <div>
                          <div style={{ color: "#64748b", fontSize: "0.9rem" }}>Price</div>
                          <div style={{ fontWeight: "600", color: "#ff7e5f" }}>{selectedPlan.price}</div>
                        </div>
                        <div>
                          <div style={{ color: "#64748b", fontSize: "0.9rem" }}>Access Duration</div>
                          <div style={{ fontWeight: "600", color: "#1e293b" }}>{selectedPlan.accessExpiration}</div>
                        </div>
                        <div>
                          <div style={{ color: "#64748b", fontSize: "0.9rem" }}>Courses Included</div>
                          <div style={{ fontWeight: "600", color: "#1e293b" }}>{selectedPlan.courses.length}</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "30px" }}>
                      <button
                        type="submit"
                        style={{
                          ...styles.btn,
                          padding: "16px 40px",
                          fontSize: "1.1rem",
                          background: selectedPlan.price === "Free"
                            ? "linear-gradient(90deg, #10b981, #34d399)"
                            : "linear-gradient(90deg, #ff7e5f, #8e44ad)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow = selectedPlan.price === "Free"
                            ? "0 8px 20px rgba(16, 185, 129, 0.3)"
                            : "0 8px 20px rgba(37, 99, 235, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        {selectedPlan.price === "Free" ? (
                          <>🚀 Submit Free Enrollment Request</>
                        ) : (
                          <>💳 Proceed to Payment</>
                        )}
                      </button>
                      <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginTop: "15px" }}>
                        <span style={{ color: "#10b981" }}>✓</span> Your information is secure and encrypted
                      </p>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: "500px" }}>
            <div style={styles.modalHeader}>
              {/* Back to Plan Details Button */}
              <button
                style={{
                  ...styles.modalBackButton,
                  ...(backHover ? styles.modalBackButtonHover : {}),
                }}
                onClick={() => setPaymentModal(null)}
                onMouseEnter={() => setBackHover(true)}
                onMouseLeave={() => setBackHover(false)}
              >
                ← Back to Details
              </button>

              {/* Close Modal Button */}
              <button
                style={{
                  ...styles.modalClose,
                  ...(closeHover ? styles.modalCloseHover : {}),
                }}
                onClick={() => setPaymentModal(null)}
                onMouseEnter={() => setCloseHover(true)}
                onMouseLeave={() => setCloseHover(false)}
              >
                ✕
              </button>
              <h2 style={styles.planName}>Payment Details</h2>
              <p style={{ color: "#64748b", marginTop: "5px" }}>{paymentModal.name}</p>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.paymentSummary}>
                <h4 style={{ marginBottom: "20px", color: "#1e293b", textAlign: "center" }}>
                  💳 Payment Summary
                </h4>

                <div style={{ marginBottom: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <span style={{ color: "#64748b" }}>Membership Plan:</span>
                    <span style={{ fontWeight: "600", color: "#1e293b" }}>{paymentModal.name}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <span style={{ color: "#64748b" }}>Price:</span>
                    <span style={{ fontWeight: "600", color: "#ff7e5f", fontSize: "1.2rem" }}>
                      {paymentModal.price}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <span style={{ color: "#64748b" }}>Access Duration:</span>
                    <span style={{ fontWeight: "600", color: "#1e293b" }}>{paymentModal.accessExpiration}</span>
                  </div>
                </div>

                <div style={{ borderTop: "2px dashed #e2e8f0", paddingTop: "20px", marginTop: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "#1e293b", fontWeight: "600" }}>Total Amount:</span>
                    <span style={{ fontWeight: "700", color: "#2563eb", fontSize: "1.4rem" }}>
                      {paymentModal.price}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ margin: "30px 0" }}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Card Number</label>
                  <input
                    type="text"
                    style={styles.formInput}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Expiry Date</label>
                    <input
                      type="text"
                      style={styles.formInput}
                      placeholder="MM/YY"
                      maxLength="5"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>CVV</label>
                    <input
                      type="text"
                      style={styles.formInput}
                      placeholder="123"
                      maxLength="3"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Cardholder Name</label>
                  <input
                    type="text"
                    style={styles.formInput}
                    placeholder="Enter name as on card"
                  />
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <button
                  onClick={handlePaymentSubmit}
                  style={{
                    ...styles.btn,
                    padding: "16px 40px",
                    fontSize: "1.1rem",
                    background: "linear-gradient(90deg, #10b981, #34d399)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  💳 Pay {paymentModal.price}
                </button>

                <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginTop: "15px" }}>
                  <span style={{ color: "#10b981" }}>🔒</span> Secure payment powered by Razorpay
                </p>

                <button
                  onClick={() => setPaymentModal(null)}
                  style={{
                    ...styles.backBtn,
                    width: "100%",
                    marginTop: "15px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  ← Back to Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}